import { useCallback, useEffect, useRef, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { apiPost, errorMessage } from "../api";
import { AppHQLogo } from "../Logo";

/** One-time setup: create the first passkey using the SETUP_TOKEN in the URL. */
export function Setup() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<"checking" | "ready" | "closed" | "busy" | "error">("checking");
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(defaultDeviceName());
  const options = useRef<PublicKeyCredentialCreationOptionsJSON | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setState("closed");
      return;
    }
    try {
      const r = await apiPost("/api/auth/setup/options", { token });
      if (r.status === 404) {
        setState("closed");
        return;
      }
      if (!r.ok) {
        setState("error");
        setError(await errorMessage(r, "Kon setup niet starten."));
        return;
      }
      options.current = ((await r.json()) as { options: PublicKeyCredentialCreationOptionsJSON }).options;
      setState("ready");
    } catch {
      setState("error");
      setError("Netwerkfout.");
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createPasskey() {
    if (!options.current) return;
    setState("busy");
    setError(null);
    try {
      const response = await startRegistration({ optionsJSON: options.current });
      const v = await apiPost("/api/auth/setup/verify", { token, response, name: name.trim() || "Mijn apparaat" });
      if (!v.ok) throw new Error(await errorMessage(v, "Aanmaken mislukt."));
      window.location.href = "/";
    } catch (e) {
      const cancelled = e instanceof Error && e.name === "NotAllowedError";
      setState("error");
      setError(cancelled ? "Geannuleerd. Probeer het opnieuw." : e instanceof Error ? e.message : "Er ging iets mis.");
      void load();
    }
  }

  return (
    <div className="auth-screen">
      <div className="glass-card">
        <div className="logo-wrap">
          <AppHQLogo size={72} />
        </div>
        <h1>AppHQ instellen</h1>
        {state === "closed" ? (
          <p className="sub">Setup is afgerond of de link is ongeldig. Ga naar de loginpagina.</p>
        ) : (
          <>
            <p className="sub">Maak je eerste passkey aan met Face ID of Touch ID.</p>
            <input
              className="btn"
              style={{ background: "var(--card)", color: "var(--ink)", marginBottom: 12, borderRadius: 14, border: "1px solid var(--line)" }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Naam van dit apparaat"
              aria-label="Naam van dit apparaat"
            />
            <button className="btn btn-primary" onClick={createPasskey} disabled={state !== "ready" && state !== "error"}>
              {state === "busy" ? <span className="spinner" /> : null}
              {state === "busy" ? "Bevestig met Face ID of Touch ID…" : "Passkey aanmaken"}
            </button>
          </>
        )}
        {error && <p className="error">{error}</p>}
        <p className="foot">Daarna werkt alleen jouw passkey. Verwijder het setup-token.</p>
      </div>
    </div>
  );
}

function defaultDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  return "Mijn apparaat";
}
